import {
  assetCommentSchema,
  assetDetailSchema,
  incrementViewResultSchema,
  ratingSchema,
  setRatingRequestSchema,
  type AssetComment,
  type AssetDetail,
  type Rating,
} from '@xphoto/shared';
import { z } from 'zod';
import { ApiError } from '../gallery/api';
import { apiFetch } from '../lib/http';
import { withSlotParam } from '../lib/activeSlot';

/**
 * Client typé des routes visionneuse & notes (T3, XPHOTO-21/-10). Comme le client
 * galerie, chaque réponse est RE-PARSÉE via son schéma partagé (« never trust the
 * client », y compris ses propres réponses) : un backend divergent lève.
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON : on garde le code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.json();
}

/** `GET /api/assets/:id` — détail complet (EXIF, tags, notes). */
export async function fetchAssetDetail(
  assetId: string,
  signal?: AbortSignal,
): Promise<AssetDetail> {
  const res = await apiFetch(`/api/assets/${assetId}`, { signal: signal ?? null });
  return assetDetailSchema.parse(await readJson(res));
}

/**
 * `POST /api/assets/:id/view` — incrémente le compteur de vues global (LOT 1).
 * Renvoie le nouveau compteur. Exempté du rate-limit côté serveur.
 */
export async function incrementView(assetId: string): Promise<number> {
  const res = await apiFetch(`/api/assets/${assetId}/view`, { method: 'POST' });
  return incrementViewResultSchema.parse(await readJson(res)).viewCount;
}

/**
 * `PATCH /api/assets/:id/rating` — fixe la note 0..5 (0 = non noté) (LOT 1).
 * Le corps est validé côté client via le schéma partagé ; la réponse `{ rating }`
 * est re-parsée.
 */
export async function setRating(assetId: string, rating: Rating): Promise<Rating> {
  const body = setRatingRequestSchema.parse({ rating });
  const res = await apiFetch(`/api/assets/${assetId}/rating`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed = z.object({ rating: ratingSchema }).parse(await readJson(res));
  return parsed.rating;
}

/**
 * `POST /api/assets/:id/comments` — ajoute un commentaire (LOT 1). Le `type` est
 * OPTIONNEL (l'UI ne le saisit plus) : omis, le backend normalise en `''`.
 */
export async function createComment(
  assetId: string,
  body: string,
  type?: string,
): Promise<AssetComment> {
  const payload = type === undefined ? { body } : { type, body };
  const res = await apiFetch(`/api/assets/${assetId}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return assetCommentSchema.parse(await readJson(res));
}

/**
 * `PATCH /api/assets/:id/comments/:commentId` — édite un commentaire (LOT 1). Le
 * `type` est OPTIONNEL (voir `createComment`).
 */
export async function updateComment(
  assetId: string,
  commentId: string,
  body: string,
  type?: string,
): Promise<AssetComment> {
  const payload = type === undefined ? { body } : { type, body };
  const res = await apiFetch(`/api/assets/${assetId}/comments/${commentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return assetCommentSchema.parse(await readJson(res));
}

/** `DELETE /api/assets/:id/comments/:commentId` — supprime un commentaire typé (LOT 1). */
export async function deleteComment(assetId: string, commentId: string): Promise<void> {
  const res = await apiFetch(`/api/assets/${assetId}/comments/${commentId}`, { method: 'DELETE' });
  await expectOk(res);
}

/** Vérifie qu'une réponse sans corps utile est OK, sinon lève un `ApiError` typé. */
async function expectOk(res: Response): Promise<void> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const b = (await res.json()) as { error?: { code?: string } };
      if (typeof b?.error?.code === 'string') code = b.error.code;
    } catch {
      // 204 sans corps : rien à lire.
    }
    throw new ApiError(res.status, code);
  }
}

/**
 * URL de téléchargement de l'original (`GET /api/assets/:id/original`). Lien
 * browser-natif (`<a href download>`, `<img src>`, `<video src>`) : le slot actif
 * passe en `?slot=N`.
 *
 * `rev` (optionnel) = CACHE-BUSTER, jumeau de celui de `thumbnailUrl`. Une écriture
 * EXIF (dont la rotation par le tag Orientation, outil « Pivoter ») change les OCTETS
 * du fichier sans changer son URL : le navigateur resservirait sa copie en cache et
 * l'ancienne orientation resterait affichée. Apposer le `rev` de l'asset (préfixe du
 * `file_hash`) donne une URL NEUVE à chaque écriture.
 *
 * La route ne déclare AUCUN schéma de query (`{ schema: { params } }` seulement) : un
 * paramètre inconnu y est donc simplement ignoré par Fastify, jamais rejeté en 400 —
 * c'est déjà le cas du `?slot=N` ci-dessous. Garde : `asset-detail.test.ts`.
 */
export function originalUrl(assetId: string, rev?: string): string {
  const base = `/api/assets/${assetId}/original`;
  const withRev = rev !== undefined && rev !== '' ? `${base}?rev=${encodeURIComponent(rev)}` : base;
  return withSlotParam(withRev);
}
